import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import PricingTrust from "@/components/landing/PricingTrust";

const features = [
  "Real-time scan of 35+ secret sources",
  "AI-scored trend feed (updated daily)",
  "Complete launch blueprint per trend",
  "Niche filters: category, capital, skill",
  "Real-time alerts on accelerating niches",
  "Supplier, template & creative suggestions",
  "Lifetime updates — every new source we add",
  "Private Discord for early adopters",
];

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openCheckout, checkoutElement } = useStripeCheckout();

  const handleClick = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    openCheckout({
      priceId: "trends_discovery_lifetime_39",
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  return (
    <section id="pricing" className="py-32">
      <div className="container">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <div className="font-mono text-xs text-primary mb-4">// ONE PAYMENT. FOREVER.</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight">
            Pay once. Outpace forever.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground">
            No subscription tax on your ambition. Lock lifetime access at the founder rate
            before it doubles.
          </p>
        </div>

        <div className="max-w-2xl mx-auto relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary via-accent to-primary rounded-2xl blur opacity-30" />
          <div className="relative rounded-2xl border border-primary/40 bg-card p-10 shadow-elevated">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
              <div>
                <div className="font-mono text-xs text-primary tracking-wider">FOUNDER ACCESS</div>
                <h3 className="font-display text-3xl font-bold mt-1">Lifetime License</h3>
              </div>
              <span className="px-3 py-1 text-xs font-mono rounded-full bg-accent/20 text-accent border border-accent/30">
                FOUNDER PRICE
              </span>
            </div>

            <div className="flex items-baseline gap-3 mb-2" data-testid="pricing-price-block">
              <span className="font-display text-6xl font-bold" data-testid="pricing-price-current">$39</span>
              <span className="text-muted-foreground line-through" data-testid="pricing-price-original">$199</span>
              <span className="text-sm text-muted-foreground">one-time</span>
            </div>

            <div className="text-sm text-muted-foreground mb-6">
              Billed once. Yours forever. No card on file.
            </div>

            <div className="mb-8 rounded-lg border border-border bg-background/50 p-4">
              <div className="font-mono text-[11px] text-primary mb-2">// WHY NO RECURRING FEES</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We're software, not a research agency. Our scrapers and AI scoring run
                whether you log in or not — so charging you monthly would be rent-seeking.
                One $39 payment covers infrastructure for life, including every new source
                and feature we ship.
              </p>
            </div>

            <Button variant="hero" size="xl" className="w-full" onClick={handleClick}>
              Claim Lifetime Access <ArrowRight />
            </Button>

            <ul className="mt-10 grid sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <span className="grid place-items-center w-5 h-5 rounded-full bg-primary/15 text-primary mt-0.5 shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-border text-center text-xs font-mono text-muted-foreground">
              14-day no-questions refund · Instant access
              <div className="mt-2 normal-case">
                Your licence key is emailed the moment payment clears —{" "}
                <Link to="/account" className="text-primary hover:underline">manage it in your account</Link>.
              </div>
              <div className="mt-2 normal-case">
                By purchasing you agree to our{" "}
                <Link to="/terms" className="text-primary hover:underline">Terms</Link> and{" "}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              </div>
            </div>
          </div>
        </div>
        <PricingTrust />
      </div>
      {checkoutElement}
    </section>
  );
};

export default Pricing;
